/**
 * Canvas drawing. Everything visual that is not a DOM element lives here.
 *
 * All of it reads from the same palette as the CSS, resolved at draw time so a
 * future theme change stays in one place.
 */

import { useEffect, useRef } from 'react';
import { MONK_SWATCHES, signalAttenuation } from '@omnialera/ori-signal';
import { useI18n } from '../lib/i18n.jsx';

const PALETTE = {
  pulse: '#f87f94',
  pulseDeep: '#8e3c4b',
  signal: '#63ccab',
  warn: '#ebb673',
  muted: '#bfadb1',
  faint: '#a28f93',
  rule: '#3a2d31',
  ruleLit: '#584447',
  ink: '#fcf8f7',
  inkSoft: '#e4d9db',
  ground: '#0a0709',
};

const prefersReducedMotion = () =>
  typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * The label face for canvas text.
 *
 * Same reasoning as the stylesheet: IBM Plex Mono cannot compose Yorùbá and
 * Igbo tone marks, and a canvas has no fallback chain worth relying on, so
 * outside English the labels are drawn in the body face instead.
 */
function mono(px) {
  const english = typeof document === 'undefined' || document.documentElement.lang === 'en';
  return english
    ? `${px}px "IBM Plex Mono", ui-monospace, monospace`
    : `${px + 1}px "Archivo", "Helvetica Neue", Arial, sans-serif`;
}

/** Fit the backing store to the CSS box at device resolution. */
function fit(canvas) {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const rect = canvas.getBoundingClientRect();
  if (rect.width < 2 || rect.height < 2) return null;
  const w = Math.round(rect.width * dpr);
  const h = Math.round(rect.height * dpr);
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
  const ctx = canvas.getContext('2d');
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, rect.width, rect.height);
  return { ctx, w: rect.width, h: rect.height };
}

/** Run `draw` every animation frame while `active`. */
function useAnimation(draw, active, deps) {
  const ref = useRef(null);
  const drawRef = useRef(draw);
  drawRef.current = draw;

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    let raf = 0;
    let last = performance.now();
    let stopped = false;

    const frame = (now) => {
      if (stopped) return;
      const dt = Math.min((now - last) / 1000, 0.1);
      last = now;
      const f = fit(canvas);
      if (f) drawRef.current(f.ctx, f.w, f.h, dt);
      if (active) raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);

    const onResize = () => {
      const f = fit(canvas);
      if (f) drawRef.current(f.ctx, f.w, f.h, 0);
    };
    window.addEventListener('resize', onResize);

    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, ...deps]);

  return ref;
}

/* ── the Twin ───────────────────────────────────────────────────────── */

/**
 * Your Ori: a form that beats at your last recovered rate, and whose halo
 * destabilises when the reading could not be trusted. The uncertainty lives in
 * the object rather than in a footnote nobody reads.
 */
export function Twin({ reading, monkTone, className = 'twin' }) {
  const { t } = useI18n();
  const phase = useRef(0);
  const bpm = useRef(72);
  const coherence = useRef(0.5);

  const ref = useAnimation(
    (ctx, W, H, dt) => {
      const target = reading ? (reading.locked ? 1 : 0.12) : 0.5;
      const targetBpm = reading?.locked ? reading.heartRateBpm : 72;
      bpm.current += (targetBpm - bpm.current) * Math.min(1, dt * 2);
      coherence.current += (target - coherence.current) * Math.min(1, dt * 1.6);
      if (!prefersReducedMotion()) phase.current += dt * (bpm.current / 60);

      const p = phase.current % 1;
      const beat =
        Math.exp(-(((p - 0.16) / 0.1) ** 2)) + 0.38 * Math.exp(-(((p - 0.42) / 0.15) ** 2));

      const cx = W / 2;
      const cy = H / 2 - 10;
      const base = Math.min(W, H) * 0.21;
      const r = base * (1 + beat * 0.22);
      const jitter = (1 - coherence.current) * 11;

      for (let i = 5; i >= 1; i--) {
        const jx = (Math.random() - 0.5) * jitter;
        const jy = (Math.random() - 0.5) * jitter;
        const alpha = (0.19 - i * 0.031) * (0.3 + 0.7 * coherence.current) * (0.5 + 0.5 * beat);
        ctx.beginPath();
        ctx.arc(cx + jx, cy + jy, r + i * base * 0.28, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(248,127,148,${Math.max(0, alpha).toFixed(3)})`;
        ctx.lineWidth = 1.4;
        ctx.stroke();
      }

      const grad = ctx.createRadialGradient(cx, cy, r * 0.1, cx, cy, r);
      grad.addColorStop(0, coherence.current > 0.5 ? PALETTE.pulse : PALETTE.pulseDeep);
      grad.addColorStop(1, MONK_SWATCHES[(monkTone ?? 1) - 1]);
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();
      ctx.strokeStyle = `rgba(252,248,247,${(0.14 + 0.36 * coherence.current).toFixed(3)})`;
      ctx.lineWidth = 1.2;
      ctx.stroke();

      ctx.fillStyle = PALETTE.muted;
      ctx.font = mono(13);
      ctx.textAlign = 'center';
      const label = !reading
        ? t('canvas.noReading')
        : reading.locked
          ? t('canvas.steady', { bpm: Math.round(reading.heartRateBpm) })
          : t('canvas.couldNotRead');
      ctx.fillText(label, W / 2, H - 18);
      ctx.textAlign = 'left';
    },
    true,
    [reading?.locked, reading?.heartRateBpm, monkTone, t],
  );

  return <canvas ref={ref} className={className} aria-label={t('canvas.noReading')} />;
}

/* ── the live trace ─────────────────────────────────────────────────── */

export function Trace({ getTrace, active, className = 'trace' }) {
  // Canvas text is drawn, not marked up, so it cannot be picked up by the
  // translation layer on its own — every string it prints is passed in.
  const { t } = useI18n();
  const ref = useAnimation(
    (ctx, W, H) => {
      const samples = getTrace();
      if (!samples || samples.length < 8) {
        ctx.fillStyle = PALETTE.faint;
        ctx.font = mono(13);
        ctx.fillText(t('canvas.traceEmpty'), 14, H / 2 + 5);
        return;
      }

      // Detrend for display only — the engine does its own.
      const n = samples.length;
      let mean = 0;
      for (let i = 0; i < n; i++) mean += samples[i];
      mean /= n;
      let min = Infinity;
      let max = -Infinity;
      for (let i = 0; i < n; i++) {
        const v = samples[i] - mean;
        if (v < min) min = v;
        if (v > max) max = v;
      }
      const span = Math.max(max - min, 1e-6);

      ctx.strokeStyle = PALETTE.pulse;
      ctx.lineWidth = 1.6;
      ctx.lineJoin = 'round';
      ctx.beginPath();
      for (let i = 0; i < n; i++) {
        const x = (i / (n - 1)) * W;
        const y = 12 + (1 - (samples[i] - mean - min) / span) * (H - 24);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
    },
    active,
    [active, t],
  );

  return <canvas ref={ref} className={className} aria-label={t('canvas.traceEmpty')} />;
}

/* ── the region overlay ─────────────────────────────────────────────── */

/**
 * The three regions Ori is actually reading, drawn where they actually are.
 *
 * This is not decoration. A person who can see the boxes follow their face
 * knows the tracker has found them; a person watching the boxes sit stubbornly
 * in the middle of the frame knows to move. The old fixed rectangle told them
 * nothing either way.
 *
 * The video element is mirrored in CSS so the preview behaves like a mirror,
 * but the sampled frame is not. Every box is therefore flipped horizontally on
 * the way to the screen — otherwise the overlay would sit on the wrong cheek.
 */
export function RegionOverlay({ getRegionBoxes, tracked, active, className = 'overlay' }) {
  const ref = useAnimation(
    (ctx, W, H) => {
      const boxes = getRegionBoxes?.() ?? [];
      const colour = tracked ? PALETTE.signal : PALETTE.warn;

      for (const box of boxes) {
        // Mirror to match the flipped preview.
        const x = (1 - (box.x + box.w)) * W;
        const y = box.y * H;
        const w = box.w * W;
        const h = box.h * H;

        ctx.strokeStyle = colour;
        ctx.lineWidth = 1.2;
        ctx.setLineDash(tracked ? [] : [4, 4]);
        ctx.strokeRect(x, y, w, h);
        ctx.setLineDash([]);

        // Corner ticks, so a thin box still reads as deliberate at a glance.
        const t = Math.min(9, w / 3, h / 3);
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(x, y + t);
        ctx.lineTo(x, y);
        ctx.lineTo(x + t, y);
        ctx.moveTo(x + w - t, y + h);
        ctx.lineTo(x + w, y + h);
        ctx.lineTo(x + w, y + h - t);
        ctx.stroke();

        ctx.fillStyle = colour;
        ctx.font = mono(11);
        ctx.fillText(box.name, x, y - 5);
      }

      if (!boxes.length) return;
      ctx.fillStyle = tracked ? PALETTE.signal : PALETTE.faint;
      ctx.font = mono(11);
      ctx.fillText(tracked ? 'face tracked' : 'estimating position', 10, H - 10);
    },
    active,
    [tracked, active],
  );

  return <canvas ref={ref} className={className} aria-hidden="true" />;
}

/* ── the simulated subject's face ───────────────────────────────────── */

export function SkinViewport({ monkTone, progress, scanning, className = 'viewport' }) {
  const { t: tr } = useI18n();
  const t = useRef(0);
  const ref = useAnimation(
    (ctx, W, H, dt) => {
      t.current += dt;
      const cx = W / 2;
      const cy = H / 2 - 6;
      const R = Math.min(W, H) * 0.28;
      const attenuation = signalAttenuation(monkTone ?? 1);

      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.fillStyle = MONK_SWATCHES[(monkTone ?? 1) - 1];
      ctx.fill();

      const beat = scanning ? 0.5 + 0.5 * Math.sin(t.current * 2 * Math.PI * 1.2) : 0.4;
      const grad = ctx.createRadialGradient(cx, cy, R * 0.05, cx, cy, R);
      grad.addColorStop(0, `rgba(248,127,148,${(0.15 + 0.78 * attenuation * beat).toFixed(3)})`);
      grad.addColorStop(1, 'rgba(248,127,148,0)');
      ctx.beginPath();
      ctx.arc(cx, cy, R, 0, Math.PI * 2);
      ctx.fillStyle = grad;
      ctx.fill();

      ctx.strokeStyle = 'rgba(252,248,247,0.2)';
      ctx.lineWidth = 1.2;
      ctx.stroke();

      if (scanning) {
        ctx.beginPath();
        ctx.arc(cx, cy, R + 18, -Math.PI / 2, -Math.PI / 2 + (progress ?? 0) * Math.PI * 2);
        ctx.strokeStyle = PALETTE.pulse;
        ctx.lineWidth = 2.5;
        ctx.stroke();
      }

      ctx.fillStyle = PALETTE.muted;
      ctx.font = mono(13);
      ctx.textAlign = 'center';
      ctx.fillText(tr('canvas.signalReaches', { percent: Math.round(attenuation * 100) }), W / 2, H - 18);
      ctx.textAlign = 'left';
    },
    true,
    [monkTone, progress, scanning, tr],
  );

  return <canvas ref={ref} className={className} aria-label={tr('canvas.signalReaches', { percent: Math.round(signalAttenuation(monkTone ?? 1) * 100) })} />;
}

/* ── trend ──────────────────────────────────────────────────────────── */

export function Trend({ readings, className = 'trend' }) {
  const ref = useAnimation(
    (ctx, W, H) => {
      const rs = readings.slice(-14);
      if (!rs.length) {
        ctx.fillStyle = PALETTE.faint;
        ctx.font = mono(13);
        ctx.fillText('no readings yet', 14, H / 2 + 5);
        return;
      }
      const values = rs.filter((r) => r.locked).map((r) => r.heartRateBpm);
      const min = Math.min(...values, 60);
      const max = Math.max(...values, 90);
      const pad = 24;
      const span = Math.max(max - min, 10);
      const X = (i) => pad + (rs.length === 1 ? 0.5 : i / (rs.length - 1)) * (W - 2 * pad);
      const Y = (v) => pad + (1 - (v - min) / span) * (H - 2 * pad - 16);

      ctx.strokeStyle = PALETTE.rule;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(pad, H - pad - 8);
      ctx.lineTo(W - pad, H - pad - 8);
      ctx.stroke();

      ctx.beginPath();
      let started = false;
      rs.forEach((r, i) => {
        if (!r.locked) {
          started = false;
          return;
        }
        const x = X(i);
        const y = Y(r.heartRateBpm);
        if (!started) {
          ctx.moveTo(x, y);
          started = true;
        } else ctx.lineTo(x, y);
      });
      ctx.strokeStyle = PALETTE.pulse;
      ctx.lineWidth = 2;
      ctx.stroke();

      rs.forEach((r, i) => {
        const x = X(i);
        if (r.locked) {
          ctx.beginPath();
          ctx.arc(x, Y(r.heartRateBpm), 3.4, 0, Math.PI * 2);
          ctx.fillStyle = PALETTE.pulse;
          ctx.fill();
        } else {
          ctx.strokeStyle = PALETTE.ruleLit;
          ctx.lineWidth = 1;
          ctx.setLineDash([3, 4]);
          ctx.beginPath();
          ctx.moveTo(x, pad);
          ctx.lineTo(x, H - pad - 8);
          ctx.stroke();
          ctx.setLineDash([]);
          ctx.fillStyle = PALETTE.faint;
          ctx.font = mono(12);
          ctx.textAlign = 'center';
          ctx.fillText('×', x, H - pad + 7);
          ctx.textAlign = 'left';
        }
      });

      ctx.fillStyle = PALETTE.muted;
      ctx.font = mono(12);
      ctx.fillText(`${Math.round(max)} bpm`, 6, pad - 4);
      ctx.textAlign = 'right';
      ctx.fillText('× = could not read', W - 6, pad - 4);
      ctx.textAlign = 'left';
    },
    false,
    [readings],
  );

  return <canvas ref={ref} className={className} aria-label="Recent readings" />;
}

/* ── coverage chart ─────────────────────────────────────────────────── */

export function CoverageChart({ coverage, className = 'chart' }) {
  const ref = useAnimation(
    (ctx, W, H) => {
      if (!coverage) {
        ctx.fillStyle = PALETTE.faint;
        ctx.font = mono(13);
        ctx.textAlign = 'center';
        ctx.fillText('waiting for the corpus…', W / 2, H / 2);
        ctx.textAlign = 'left';
        return;
      }

      const padL = 54;
      const padR = 16;
      const padT = 24;
      const padB = 60;
      const pw = W - padL - padR;
      const ph = H - padT - padB;

      const ours = coverage.byTone.share.map((s) => s * 100);
      const published = coverage.reference.publishedDatasetShare;
      const world = coverage.reference.worldPopulationShare;
      const maxV = Math.max(20, Math.ceil(Math.max(...ours, ...published, ...world) / 5) * 5);

      const X = (i) => padL + ((i + 0.5) / 10) * pw;
      const Y = (v) => padT + (1 - v / maxV) * ph;

      ctx.font = mono(12);
      for (let g = 0; g <= 4; g++) {
        const v = (maxV * g) / 4;
        const y = Y(v);
        ctx.strokeStyle = g === 0 ? PALETTE.ruleLit : PALETTE.rule;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(padL, y);
        ctx.lineTo(W - padR, y);
        ctx.stroke();
        ctx.fillStyle = PALETTE.muted;
        ctx.textAlign = 'right';
        ctx.fillText(`${v.toFixed(0)}%`, padL - 9, y + 4);
      }
      ctx.textAlign = 'left';
      ctx.fillStyle = PALETTE.inkSoft;
      ctx.fillText('share of readings', padL, padT - 8);

      const bw = (pw / 10) * 0.44;
      ours.forEach((v, i) => {
        const y = Y(v);
        ctx.fillStyle = PALETTE.pulse;
        ctx.fillRect(X(i) - bw / 2, y, bw, H - padB - y);
      });

      const line = (arr, colour, dash) => {
        ctx.beginPath();
        arr.forEach((v, i) => {
          const x = X(i);
          const y = Y(v);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.strokeStyle = colour;
        ctx.lineWidth = 2;
        ctx.setLineDash(dash);
        ctx.stroke();
        ctx.setLineDash([]);
        arr.forEach((v, i) => {
          ctx.beginPath();
          ctx.arc(X(i), Y(v), 3, 0, Math.PI * 2);
          ctx.fillStyle = colour;
          ctx.fill();
        });
      };
      line(published, PALETTE.warn, [6, 4]);
      line(world, PALETTE.signal, []);

      for (let i = 0; i < 10; i++) {
        ctx.fillStyle = MONK_SWATCHES[i];
        ctx.fillRect(X(i) - pw / 26, H - padB + 14, pw / 13, 15);
        ctx.fillStyle = PALETTE.muted;
        ctx.textAlign = 'center';
        ctx.font = mono(12);
        ctx.fillText(String(i + 1), X(i), H - padB + 46);
      }
      ctx.textAlign = 'center';
      ctx.fillStyle = PALETTE.faint;
      ctx.fillText('lightest  →  darkest', W / 2, H - 8);
      ctx.textAlign = 'left';
    },
    false,
    [coverage],
  );

  return <canvas ref={ref} className={className} aria-label="Corpus coverage by skin tone" />;
}

/* ── performance chart ──────────────────────────────────────────────── */

export function PerformanceChart({ byTone, className = 'chart' }) {
  const ref = useAnimation(
    (ctx, W, H) => {
      const rows = byTone ?? [];
      const hasData = rows.some((r) => r.readings > 0);
      if (!hasData) {
        ctx.fillStyle = PALETTE.faint;
        ctx.font = mono(13);
        ctx.textAlign = 'center';
        ctx.fillText('no donated readings yet', W / 2, H / 2);
        ctx.textAlign = 'left';
        return;
      }

      const padL = 56;
      const padR = 58;
      const padT = 24;
      const padB = 60;
      const pw = W - padL - padR;
      const ph = H - padT - padB;

      const gaps = rows.map((r) => r.meanConventionalGapBpm ?? 0);
      const maxGap = Math.max(20, Math.ceil(Math.max(...gaps) / 10) * 10);

      const X = (tone) => padL + ((tone - 0.5) / 10) * pw;
      const YG = (v) => padT + (1 - Math.min(v, maxGap) / maxGap) * ph;
      const YL = (v) => padT + (1 - v) * ph;

      ctx.font = mono(12);
      for (let g = 0; g <= 4; g++) {
        const y = padT + (ph * g) / 4;
        ctx.strokeStyle = g === 4 ? PALETTE.ruleLit : PALETTE.rule;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(padL, y);
        ctx.lineTo(W - padR, y);
        ctx.stroke();
        ctx.fillStyle = PALETTE.pulse;
        ctx.textAlign = 'right';
        ctx.fillText(String(Math.round(maxGap * (1 - g / 4))), padL - 9, y + 4);
        ctx.fillStyle = PALETTE.signal;
        ctx.textAlign = 'left';
        ctx.fillText(`${Math.round(100 * (1 - g / 4))}%`, W - padR + 9, y + 4);
      }
      ctx.textAlign = 'left';
      ctx.fillStyle = PALETTE.pulse;
      ctx.fillText('conventional error (bpm)', padL, padT - 8);
      ctx.textAlign = 'right';
      ctx.fillStyle = PALETTE.signal;
      ctx.fillText('success rate', W - padR, padT - 8);
      ctx.textAlign = 'left';

      const bw = (pw / 10) * 0.42;
      rows.forEach((r) => {
        if (r.lockRate === null) return;
        const y = YL(r.lockRate);
        ctx.fillStyle = 'rgba(99,204,171,0.32)';
        ctx.fillRect(X(r.monkTone) - bw / 2, y, bw, padT + ph - y);
        ctx.fillStyle = PALETTE.signal;
        ctx.fillRect(X(r.monkTone) - bw / 2, y, bw, 2.5);
      });

      const withGap = rows.filter((r) => r.meanConventionalGapBpm !== null);
      if (withGap.length) {
        ctx.beginPath();
        withGap.forEach((r, i) => {
          const x = X(r.monkTone);
          const y = YG(r.meanConventionalGapBpm);
          if (i === 0) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
        });
        ctx.strokeStyle = PALETTE.pulse;
        ctx.lineWidth = 2.2;
        ctx.stroke();
        withGap.forEach((r) => {
          ctx.beginPath();
          ctx.arc(X(r.monkTone), YG(r.meanConventionalGapBpm), 3.4, 0, Math.PI * 2);
          ctx.fillStyle = PALETTE.pulse;
          ctx.fill();
          ctx.strokeStyle = PALETTE.ground;
          ctx.lineWidth = 1.6;
          ctx.stroke();
        });
      }

      for (let i = 0; i < 10; i++) {
        ctx.fillStyle = MONK_SWATCHES[i];
        ctx.fillRect(X(i + 1) - pw / 26, H - padB + 14, pw / 13, 15);
        ctx.fillStyle = PALETTE.muted;
        ctx.textAlign = 'center';
        ctx.font = mono(12);
        ctx.fillText(String(i + 1), X(i + 1), H - padB + 46);
      }
      ctx.textAlign = 'center';
      ctx.fillStyle = PALETTE.faint;
      ctx.fillText('lightest  →  darkest', W / 2, H - 8);
      ctx.textAlign = 'left';
    },
    false,
    [byTone],
  );

  return <canvas ref={ref} className={className} aria-label="Engine performance by skin tone" />;
}
